import EditorSettings from '../ui/controls/EditorSettings'
import { store } from '../../stores/store'
import TextHeadline from '../ui/texts/TextHeadline'
import clsx from 'clsx'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { GetGameDocument } from '@/src/lib/graphql/generated/graphql'
import TabControls from '../ui/controls/TabControls'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconChevron from '../ui/icons/IconChevron'
import IconSettings from '../ui/icons/IconSettings'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import { containerVariants, tabButtonVariants, tabContainerVariants } from '@/src/lib/motionVariants'
import { useEffect, useState } from 'react'
import { useHintWooshSound, useSlideInWooshSound } from '@/src/game/audio/uiSounds'

type ConfiguratorScreenProps = {
    onNext: () => void
    onBack: () => void
}

const headlineVariants: Variants = {
    hidden: {
        opacity: 0,
        y: -100,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

const editorSettingsVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 300,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

const rotationHandVariants: Variants = {
    hidden: {
        opacity: 0,
        scale: 0.9,
        y: 12,
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

export default function ConfiguratorScreen({ onNext, onBack }: ConfiguratorScreenProps) {
    const isStele = useSteleBreakpoint();
    const gameData = store.getCachedQuery(GetGameDocument);
    const [showRotationHand, setShowRotationHand] = useState(false)

    useSlideInWooshSound(!isStele)
    useHintWooshSound(showRotationHand)

    useEffect(() => {
        const showHintTimeout = window.setTimeout(() => {
            setShowRotationHand(true)
        }, 3000)

        const handleRotation = (e: Event) => {
            const { rotation } = (e as CustomEvent<{ rotation: number }>).detail

            if (Math.abs(rotation) > 0.08) {
                window.clearTimeout(showHintTimeout)
                setShowRotationHand(false)
            }
        }

        window.addEventListener('configurator-rotation-change', handleRotation)

        return () => {
            window.clearTimeout(showHintTimeout)
            window.removeEventListener('configurator-rotation-change', handleRotation)
        }
    }, [])


    return (
        <motion.div
            className={clsx(
                'absolute inset-0 flex flex-col items-center pointer-events-none',
                'p-8',
                'stele:p-32'
            )}
            variants={containerVariants}
            initial='hidden'
            animate='visible'
        >
            {!isStele &&
                <motion.div variants={headlineVariants} className='flex flex-col justify-center items-center'>
                    <TextHeadline colorPreset='red' fontSize={isStele ? 128 : 32} leading={0}>Wie ist</TextHeadline>
                    <TextHeadline colorPreset='red' fontSize={isStele ? 128 : 32} leading={1} >dein Style?</TextHeadline>
                </motion.div>
            }

            <TabControls variants={tabContainerVariants} position={isStele ? 'leftBottom' : 'left'}>
                <motion.div variants={tabButtonVariants}>
                    <ButtonIcon color='red' icon={<IconChevron width='100%' height='100%' />} onClick={onBack} />
                </motion.div>
            </TabControls>

            {!isStele &&
                <TabControls variants={tabContainerVariants} >
                    <motion.div variants={tabButtonVariants}>
                        <ButtonIcon color='blue' icon={<IconSettings width='100%' height='100%' />} onClick={store.openSettings} />
                    </motion.div>
                </TabControls>
            }
            <div
                data-configurator-drag-zone
                className={clsx(
                    'z-0 absolute inset-x-0 touch-none pointer-events-auto select-none',
                    'top-[14%] bottom-[34%]',
                    'stele:top-[18%] stele:bottom-[30%]',
                )}
                style={{ touchAction: 'none' }}
                aria-hidden
            />

            <motion.div
                variants={editorSettingsVariants}
                className={clsx(
                    'absolute w-full pointer-events-auto',
                    'bottom-7.5 max-w-143.5',
                    'stele:bottom-30 stele:max-w-450'
                )}
            >
                <EditorSettings
                    onPrimaryAction={onNext}
                    onRandomizeAppearance={() => store.randomizeCharacterCustomization()}
                />
            </motion.div>

            <AnimatePresence>
                {showRotationHand ? (
                    <motion.div
                        key='rotation-hand'
                        variants={rotationHandVariants}
                        initial='hidden'
                        animate='visible'
                        exit={{
                            opacity: 0,
                            scale: 0.92,
                            transition: {
                                duration: 0.18,
                                ease: 'easeOut',
                            },
                        }}
                        className='z-10 absolute inset-0 flex justify-center items-center mb-60 stele:mb-240 pointer-events-none'
                    >
                        <motion.div
                            animate={{
                                x: isStele ? [-500, 500, -500] : [-150, 150, -150],
                                y: isStele ? [760, 940, 760, 940, 760] : [160, 240, 160, 240, 160],
                                scale: [0.75, 1.0, 0.75, 1.0, 0.75],
                                opacity: [0.0, 1.0, 1.0, 1.0, 0.0],
                            }}
                            transition={{
                                duration: 3.0,
                                delay: 2.5,
                                repeatDelay: 2.0,
                                repeat: Infinity,
                                repeatType: 'loop',
                                ease: 'easeInOut',
                            }}
                        >
                            <img
                                src='/images/hands/State=Point.png'
                                alt='hand'
                                className={clsx(
                                    'w-16 h-16',
                                    'stele:w-60 stele:h-60'
                                )}
                            />
                        </motion.div>
                    </motion.div>
                ) : null}
            </AnimatePresence>

        </motion.div>
    )
}

